import { v4 as uuidv4 } from 'uuid';
import type { TaskGeometry } from './TaskGeometry';
import { GeometryType } from './GeometryType';
import { ShapeType } from './ShapeType';
import { LineStyle } from './LineStyle';
import { LineType } from './LineType';
import { FillType } from './FillType';
import { MarkerShape, type Coordinates } from '../index';
import {
  DEFAULT_MARKER_COLOR,
  DEFAULT_LINE_COLOR,
  LINE_DEFAULT_WIDTH,
  DEFAULT_SHAPE_FILL_COLOR,
  SHAPE_DEFAULT_BORDER_WIDTH,
} from '../../constants';

/**
 * Creates a default marker geometry configuration
 *
 * @param coordinates - The marker coordinates
 * @param preferences - Optional user preferences (shape, color, icon, name). Falls back to defaults if not provided.
 * @returns TaskGeometry configured for a marker
 *
 * @example
 * ```typescript
 * const geometry = createDefaultMarkerGeometry({ lat: 52, lng: 20 });
 * const customGeometry = createDefaultMarkerGeometry(
 *   { lat: 52, lng: 20 },
 *   { shape: MarkerShape.Square, color: '#FF0000', icon: 'road', name: 'Main entrance' }
 * );
 * ```
 */
export const createDefaultMarkerGeometry = (
  coordinates: Coordinates,
  preferences?: {
    shape?: MarkerShape;
    color?: string;
    icon?: string;
    name?: string;
  }
): TaskGeometry => {
  return {
    id: uuidv4(),
    type: GeometryType.Marker,
    name: preferences?.name ?? '', // Empty string if not provided (will be auto-generated later)
    description: '',
    markerShape: preferences?.shape ?? MarkerShape.Circle,
    markerColor: preferences?.color ?? DEFAULT_MARKER_COLOR,
    markerIcon: preferences?.icon,
    calculatedCenter: coordinates,
  };
};

/**
 * Creates a default line geometry configuration
 *
 * @param points - Array of line points (min 2)
 * @param calculatedCenter - Optional pre-calculated center (will be computed if not provided)
 * @param preferences - Optional user preferences (type, style, width, color, name). Falls back to defaults if not provided.
 * @returns TaskGeometry configured for a line
 *
 * @example
 * ```typescript
 * const geometry = createDefaultLineGeometry([
 *   { lat: 52, lng: 20 },
 *   { lat: 52.1, lng: 20.1 },
 * ]);
 * const customGeometry = createDefaultLineGeometry(
 *   points,
 *   center,
 *   { type: LineType.Curved, style: LineStyle.Dashed, width: 5, color: '#0000FF', name: 'Main road' }
 * );
 * ```
 */
export const createDefaultLineGeometry = (
  points: Coordinates[],
  calculatedCenter?: Coordinates,
  preferences?: {
    type?: LineType;
    style?: LineStyle;
    width?: number;
    color?: string;
    name?: string;
  }
): TaskGeometry => {
  // Calculate center if not provided
  let center: Coordinates;
  if (calculatedCenter) {
    center = calculatedCenter;
  } else if (points.length >= 2) {
    const avgLat = points.reduce((sum, p) => sum + p.lat, 0) / points.length;
    const avgLng = points.reduce((sum, p) => sum + p.lng, 0) / points.length;
    center = { lat: avgLat, lng: avgLng };
  } else {
    // Fallback to first point if we have at least one
    center = points[0] || { lat: 0, lng: 0 };
  }

  return {
    id: uuidv4(),
    type: GeometryType.Line,
    name: preferences?.name ?? '', // Empty string if not provided (will be auto-generated later)
    description: '',
    linePoints: points,
    lineType: preferences?.type ?? LineType.Straight,
    lineStyle: preferences?.style ?? LineStyle.Solid,
    lineWidth: preferences?.width ?? LINE_DEFAULT_WIDTH,
    lineColor: preferences?.color ?? DEFAULT_LINE_COLOR,
    centerMarker: {
      visible: false,
      shape: MarkerShape.Circle,
      color: DEFAULT_MARKER_COLOR,
    },
    calculatedCenter: center,
  };
};

/**
 * Creates a default shape geometry configuration
 *
 * @param shapeType - The type of shape (rectangle/circle/triangle)
 * @param points - Array of shape defining points
 * @param calculatedCenter - Optional pre-calculated center
 * @param preferences - Optional user preferences (fill, fillColor, borderStyle, borderWidth, borderColor, name). Falls back to defaults if not provided.
 * @returns TaskGeometry configured for a shape
 *
 * @example
 * ```typescript
 * // Rectangle
 * const geometry = createDefaultShapeGeometry(ShapeType.Rectangle, [
 *   { lat: 52, lng: 20 },     // Top-left
 *   { lat: 52, lng: 20.1 },   // Top-right
 *   { lat: 51.9, lng: 20.1 }, // Bottom-right
 *   { lat: 51.9, lng: 20 },   // Bottom-left
 * ]);
 * const customGeometry = createDefaultShapeGeometry(
 *   ShapeType.Circle,
 *   points,
 *   center,
 *   { fill: FillType.Solid, fillColor: '#00FF00', borderWidth: 5, name: 'Building area' }
 * );
 * ```
 */
export const createDefaultShapeGeometry = (
  shapeType: ShapeType,
  points: Coordinates[],
  calculatedCenter?: Coordinates,
  preferences?: {
    fill?: FillType;
    fillColor?: string;
    borderStyle?: LineStyle;
    borderWidth?: number;
    borderColor?: string;
    name?: string;
  }
): TaskGeometry => {
  // Calculate center if not provided
  let center: Coordinates;
  if (calculatedCenter) {
    center = calculatedCenter;
  } else if (points.length >= 2) {
    if (shapeType === ShapeType.Circle) {
      // For circle, first point is the center
      const firstPoint = points[0];
      center = firstPoint || { lat: 0, lng: 0 };
    } else {
      // For rectangle and triangle, calculate average
      const avgLat = points.reduce((sum, p) => sum + p.lat, 0) / points.length;
      const avgLng = points.reduce((sum, p) => sum + p.lng, 0) / points.length;
      center = { lat: avgLat, lng: avgLng };
    }
  } else if (points.length === 1) {
    // Fallback to first point
    const firstPoint = points[0];
    center = firstPoint || { lat: 0, lng: 0 };
  } else {
    // Empty array fallback
    center = { lat: 0, lng: 0 };
  }

  return {
    id: uuidv4(),
    type: GeometryType.Shape,
    name: preferences?.name ?? '', // Empty string if not provided (will be auto-generated later)
    description: '',
    shapeType,
    shapePoints: points,
    shapeFill: preferences?.fill ?? FillType.None,
    shapeFillColor: preferences?.fillColor ?? DEFAULT_SHAPE_FILL_COLOR,
    shapeBorderStyle: preferences?.borderStyle ?? LineStyle.Solid,
    shapeBorderWidth: preferences?.borderWidth ?? SHAPE_DEFAULT_BORDER_WIDTH,
    shapeBorderColor: preferences?.fillColor ?? DEFAULT_SHAPE_FILL_COLOR, // Border = Fill (same color)
    centerMarker: {
      visible: false,
      shape: MarkerShape.Circle,
      color: DEFAULT_MARKER_COLOR,
    },
    calculatedCenter: center,
  };
};
